import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Building2,
  HardHat,
  Zap,
  DollarSign,
  Shield,
  CheckCircle2,
  Search,
  ArrowRight,
  Sparkles,
  MapPin,
  Clock,
  Users,
} from "lucide-react";

// Force static generation
export const dynamic = 'force-static';

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Orange accent bar */}
      <div className="h-1 bg-primary" />

      {/* Header */}
      <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto flex h-14 max-w-screen-xl items-center justify-between px-4">
          <Link href="/" className="flex items-center gap-2">
            <img src="/logo_black.svg" alt="RateRight" className="h-8" />
          </Link>
          <Link href="/auth/login">
            <Button variant="ghost" size="sm">
              Sign in
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="px-4 pt-10 pb-12">
        <div className="mx-auto max-w-screen-sm">
          <div className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-3 py-1 text-sm font-medium text-primary">
            <MapPin className="h-3.5 w-3.5" />
            Now live in Sydney
          </div>

          <h1 className="mt-4 text-[2rem] font-extrabold leading-[1.1] tracking-tight sm:text-5xl">
            Hire construction workers{" "}
            <span className="text-primary">in seconds</span>
          </h1>

          <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
            AI builds profiles and finds matches.
            You just tap hire. <strong className="text-foreground">$50 flat fee</strong> per hire.
          </p>

          {/* CTA Buttons - BIG touch targets */}
          <div className="mt-8 flex flex-col gap-3">
            <Link href="/contractor/signup">
              <Button className="h-14 w-full gap-3 text-base font-semibold shadow-lg shadow-primary/25">
                <Building2 className="h-6 w-6" />
                I&apos;m Hiring
                <ArrowRight className="ml-auto h-5 w-5" />
              </Button>
            </Link>
            <Link href="/worker/signup">
              <Button
                variant="outline"
                className="h-14 w-full gap-3 border-2 text-base font-semibold"
              >
                <HardHat className="h-6 w-6" />
                I&apos;m Looking For Work
                <ArrowRight className="ml-auto h-5 w-5" />
              </Button>
            </Link>
          </div>

          <p className="mt-4 text-center text-sm text-muted-foreground">
            Free to sign up. Workers always free.
          </p>
        </div>
      </section>

      {/* How it works */}
      <section className="border-t bg-muted/50 px-4 py-12">
        <div className="mx-auto max-w-screen-sm">
          <h2 className="text-center text-2xl font-bold tracking-tight">
            How it works
          </h2>
          <p className="mt-2 text-center text-muted-foreground">
            Three steps. Under two minutes.
          </p>

          <div className="mt-8 space-y-6">
            <Step
              number="1"
              icon={<Search className="h-5 w-5" />}
              title="Tell us your company"
              description="Enter your company name or ABN. AI researches your business and builds your profile automatically."
            />
            <Step
              number="2"
              icon={<Sparkles className="h-5 w-5" />}
              title="AI finds the best matches"
              description="Post a job in 60 seconds. Smart matching ranks workers by distance, experience, and ratings."
            />
            <Step
              number="3"
              icon={<CheckCircle2 className="h-5 w-5" />}
              title="One tap to hire"
              description="See top matches, tap hire, worker accepts. $50 flat fee — only charged when you hire."
            />
          </div>
        </div>
      </section>

      {/* For Contractors */}
      <section className="px-4 py-12">
        <div className="mx-auto max-w-screen-sm">
          <div className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-bold text-primary">For Contractors</h2>
          </div>
          <h3 className="mt-1 text-2xl font-bold tracking-tight">
            Stop wasting time hiring
          </h3>

          <div className="mt-6 space-y-3">
            <FeatureCard
              icon={<Zap className="h-5 w-5 text-primary" />}
              title="AI does the work"
              description="Company research, job descriptions, worker matching — all automated."
            />
            <FeatureCard
              icon={<DollarSign className="h-5 w-5 text-primary" />}
              title="$50 flat fee per hire"
              description="No subscriptions, no percentages. Free to post. Only pay when you hire."
            />
            <FeatureCard
              icon={<Shield className="h-5 w-5 text-primary" />}
              title="Verified workers"
              description="White Card verification, ratings, and real experience on every profile."
            />
            <FeatureCard
              icon={<Clock className="h-5 w-5 text-primary" />}
              title="Hire by tomorrow"
              description="Post a job now, get matched in minutes. Workers on site tomorrow."
            />
          </div>
        </div>
      </section>

      {/* For Workers */}
      <section className="border-t bg-muted/50 px-4 py-12">
        <div className="mx-auto max-w-screen-sm">
          <div className="flex items-center gap-2">
            <HardHat className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-bold text-primary">For Workers</h2>
          </div>
          <h3 className="mt-1 text-2xl font-bold tracking-tight">
            Get on a job fast
          </h3>
          <p className="mt-2 text-muted-foreground">
            Sign up free. Get matched to jobs near you. Know what you&apos;ll earn.
          </p>

          <div className="mt-6 space-y-3">
            <Benefit
              icon={<DollarSign className="h-4 w-4" />}
              text="Always free — $0 to sign up, $0 to get hired"
            />
            <Benefit
              icon={<Clock className="h-4 w-4" />}
              text="Answer 5 questions, your profile is done in 2 minutes"
            />
            <Benefit
              icon={<MapPin className="h-4 w-4" />}
              text="Get matched to jobs within kilometres of you"
            />
            <Benefit
              icon={<Users className="h-4 w-4" />}
              text="See the pay rate upfront — no guessing"
            />
          </div>

          <Link href="/worker/signup">
            <Button className="mt-8 h-12 w-full gap-2 text-base font-semibold">
              <HardHat className="h-5 w-5" />
              Sign up free
            </Button>
          </Link>
        </div>
      </section>

      {/* Pricing */}
      <section className="px-4 py-12">
        <div className="mx-auto max-w-screen-sm text-center">
          <h2 className="text-2xl font-bold tracking-tight">Simple pricing</h2>
          <p className="mt-2 text-muted-foreground">
            No hidden fees. No lock-in. No percentages.
          </p>

          <Card className="mt-6 border-2 border-primary/20">
            <CardContent className="pt-8 pb-8">
              <p className="text-6xl font-extrabold text-primary">$50</p>
              <p className="mt-2 text-lg text-muted-foreground">
                per hire — that&apos;s it
              </p>
              <div className="mx-auto mt-6 max-w-xs space-y-3 text-left">
                <BenefitCheck text="Free to post jobs" />
                <BenefitCheck text="Free to browse workers" />
                <BenefitCheck text="Free for workers — always" />
                <BenefitCheck text="Only pay when you confirm a hire" />
                <BenefitCheck text="No monthly subscriptions" />
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-primary px-4 py-14 text-primary-foreground">
        <div className="mx-auto max-w-screen-sm text-center">
          <h2 className="text-2xl font-bold">Ready to hire smarter?</h2>
          <p className="mt-2 opacity-80">
            Join Sydney contractors already using RateRight.
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Link href="/contractor/signup" className="w-full sm:w-auto">
              <Button
                variant="secondary"
                className="h-12 w-full gap-2 text-base font-semibold"
              >
                <Building2 className="h-5 w-5" />
                Start hiring
              </Button>
            </Link>
            <Link href="/worker/signup" className="w-full sm:w-auto">
              <Button
                variant="secondary"
                className="h-12 w-full gap-2 text-base font-semibold"
              >
                <HardHat className="h-5 w-5" />
                Find work
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t px-4 py-8">
        <div className="mx-auto max-w-screen-sm">
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row sm:justify-between">
            <div className="flex items-center justify-center gap-2">
              <img src="/logo_black.svg" alt="RateRight" className="h-6" />
            </div>
            <div className="flex items-center gap-6 text-sm text-muted-foreground">
              <Link href="/faq" className="hover:text-foreground">
                FAQ
              </Link>
              <a
                href="mailto:support@rateright.com.au"
                className="hover:text-foreground"
              >
                Contact
              </a>
            </div>
          </div>
          <p className="mt-4 text-center text-sm text-muted-foreground">
            The simplest way to hire construction workers in Australia.
          </p>
          <p className="mt-2 text-center text-xs text-muted-foreground">
            &copy; {new Date().getFullYear()} RateRight Pty Ltd. All rights
            reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

function Step({
  number,
  icon,
  title,
  description,
}: {
  number: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex gap-4">
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-sm font-bold text-primary-foreground">
        {number}
      </div>
      <div className="pt-1">
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-semibold">{title}</h3>
        </div>
        <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
          {description}
        </p>
      </div>
    </div>
  );
}

function FeatureCard({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex gap-4 rounded-xl border bg-card p-4">
      <div className="mt-0.5 shrink-0">{icon}</div>
      <div>
        <h3 className="font-semibold">{title}</h3>
        <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}

function Benefit({
  icon,
  text,
}: {
  icon: React.ReactNode;
  text: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 shrink-0 text-primary">{icon}</div>
      <span className="text-sm">{text}</span>
    </div>
  );
}

function BenefitCheck({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2">
      <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
      <span className="text-sm">{text}</span>
    </div>
  );
}
